import moment, { Moment } from 'moment-timezone'
import { logWarn } from 'lib/logging'

const _getFormat = (value: number | string | Date | null | moment.Moment): string => {
  // using startOf(`day`) to avoid it returning i.e. `Today` even though it's one calendar day ago (but less than 24 hours ago)
  let dateDifference = moment(value).startOf(`day`).diff(moment().startOf(`day`), `days`)
  if (dateDifference === 0) {
    return `[Today at] h:mm A`
  } else if (dateDifference === -1) {
    return `[Yesterday at] h:mm A`
  }
  return `MMM D, YYYY h:mm A`
}

export function formatDate({
  value,
  dateOnly,
  format,
  useLocalUtc,
}: {
  value?: number | string | Date | null | moment.Moment
  dateOnly?: boolean
  format?: string
  useLocalUtc?: boolean
}): string | undefined {
  if (!value) {
    return ``
  }
  if (!moment(value).isValid()) {
    logWarn(`Date input is invalid`, value)
    return `${value}`
  }

  let date = moment.utc(value)
  if (useLocalUtc) {
    const localTimezone = moment.tz.guess()
    const utcOffset = moment.tz(localTimezone).utcOffset()
    date = date.utcOffset(utcOffset)
  }

  if (date.hours() === 0 && date.minutes() === 0) {
    dateOnly = true
  }

  return dateOnly ? date.format(format || `MMM D, YYYY`) : date.format(format || _getFormat(value))
}

export function getAge(birthDate: number | string | Date | moment.Moment) {
  let age = moment().diff(birthDate, `years`)
  return age ?? ``
}

/* eslint-disable quotes */
export enum DateRangeOperator {
  'custom',
  'yesterday',
  'today',
  'this_week',
  'last_week',
  'this_month',
  'last_month',
  'this_year',
  'last_year',
  'last_30_days',
  'last_90_days',
  'last_6_months',
  'last_12_months',
}
/* eslint-enable quotes */

export const DATE_RANGE_PRESET_OPTIONS = [
  { value: DateRangeOperator.custom, display: `Custom Date` },
  { value: DateRangeOperator.today, display: `Today` },
  { value: DateRangeOperator.yesterday, display: `Yesterday` },
  { value: DateRangeOperator.this_week, display: `This Week` },
  { value: DateRangeOperator.last_week, display: `Last Week` },
  { value: DateRangeOperator.this_month, display: `This Month` },
  { value: DateRangeOperator.last_month, display: `Last Month` },
  { value: DateRangeOperator.last_30_days, display: `Last 30 Days` },
  { value: DateRangeOperator.last_90_days, display: `Last 90 Days` },
  { value: DateRangeOperator.last_6_months, display: `Last 6 months` },
  { value: DateRangeOperator.last_12_months, display: `Last 12 months` },
  { value: DateRangeOperator.this_year, display: `This Year` },
  { value: DateRangeOperator.last_year, display: `Last Year` },
]

export function getDateRangeValues(selectorPreset: DateRangeOperator): [Moment, Moment] {
  let fromDate: Moment | null = moment()
  let toDate: Moment | null = moment()
  switch (selectorPreset) {
    case DateRangeOperator.yesterday:
      fromDate = moment().subtract(1, `day`).startOf(`day`)
      toDate = moment().subtract(1, `day`).endOf(`day`)
      break
    case DateRangeOperator.today:
      fromDate = moment().startOf(`day`)
      toDate = moment()
      break
    case DateRangeOperator.this_week:
      fromDate = moment().startOf(`week`)
      toDate = moment().endOf(`week`)
      break
    case DateRangeOperator.last_week:
      fromDate = moment().subtract(1, `week`).startOf(`week`)
      toDate = moment().subtract(1, `week`).endOf(`week`)
      break
    case DateRangeOperator.this_month:
      fromDate = moment().startOf(`month`)
      toDate = moment().endOf(`month`)
      break
    case DateRangeOperator.last_month:
      fromDate = moment().subtract(1, `month`).startOf(`month`)
      toDate = moment().subtract(1, `month`).endOf(`month`)
      break
    case DateRangeOperator.last_30_days:
      fromDate = moment().subtract(30, `days`).startOf(`day`)
      toDate = moment()
      break
    case DateRangeOperator.last_90_days:
      fromDate = moment().subtract(90, `days`).startOf(`day`)
      toDate = moment()
      break
    case DateRangeOperator.last_6_months:
      fromDate = moment().subtract(6, `months`).startOf(`month`)
      toDate = moment()
      break
    case DateRangeOperator.last_12_months:
      fromDate = moment().subtract(12, `months`).startOf(`month`)
      toDate = moment()
      break
    case DateRangeOperator.this_year:
      fromDate = moment().startOf(`year`)
      toDate = moment()
      break
    case DateRangeOperator.last_year:
      fromDate = moment().subtract(1, `year`).startOf(`year`)
      toDate = moment().subtract(1, `year`).endOf(`year`)
      break
    default:
      break
  }
  return [fromDate, toDate]
}

export function inferDateRangePreset(
  fromDate: Moment,
  toDate: Moment,
  limitToMonth: boolean = false,
): DateRangeOperator {
  const granularity = (limitToMonth ? `month` : `day`) as moment.unitOfTime.StartOf

  if (
    fromDate.isSame(moment().subtract(1, `day`).startOf(`day`), `day`) &&
    toDate.isSame(moment().subtract(1, `day`).endOf(`day`), `day`)
  ) {
    return DateRangeOperator.yesterday
  } else if (fromDate.isSame(moment().startOf(`day`), `day`) && toDate.isSame(moment().endOf(`day`), `day`)) {
    return DateRangeOperator.today
  } else if (fromDate.isSame(moment().startOf(`week`), `day`) && toDate.isSame(moment().endOf(`week`), `day`)) {
    return DateRangeOperator.this_week
  } else if (
    fromDate.isSame(moment().subtract(1, `week`).startOf(`week`), `day`) &&
    toDate.isSame(moment().subtract(1, `week`).endOf(`week`), `day`)
  ) {
    return DateRangeOperator.last_week
  } else if (
    fromDate.isSame(moment().startOf(`month`), granularity) &&
    toDate.isSame(moment().endOf(`month`), granularity)
  ) {
    return DateRangeOperator.this_month
  } else if (
    fromDate.isSame(moment().subtract(1, `month`).startOf(`month`), granularity) &&
    toDate.isSame(moment().subtract(1, `month`).endOf(`month`), granularity)
  ) {
    return DateRangeOperator.last_month
  } else if (
    fromDate.isSame(moment().subtract(30, `days`).startOf(`day`), granularity) &&
    toDate.isSame(moment(), granularity)
  ) {
    return DateRangeOperator.last_30_days
  } else if (
    fromDate.isSame(moment().subtract(90, `days`).startOf(`day`), granularity) &&
    toDate.isSame(moment(), granularity)
  ) {
    return DateRangeOperator.last_90_days
  } else if (
    fromDate.isSame(moment().subtract(6, `months`).startOf(`month`), granularity) &&
    toDate.isSame(moment(), granularity)
  ) {
    return DateRangeOperator.last_6_months
  } else if (
    fromDate.isSame(moment().subtract(12, `months`).startOf(`month`), granularity) &&
    toDate.isSame(moment(), granularity)
  ) {
    return DateRangeOperator.last_12_months
  } else if (fromDate.isSame(moment().startOf(`year`), granularity) && toDate.isSame(moment(), granularity)) {
    return DateRangeOperator.this_year
  } else if (
    fromDate.isSame(moment().subtract(1, `year`).startOf(`year`), granularity) &&
    toDate.isSame(moment().subtract(1, `year`).endOf(`year`), granularity)
  ) {
    return DateRangeOperator.last_year
  } else {
    return DateRangeOperator.custom
  }
}
